<template>
  <BittsModal
    v-if="visible || showDeleteReportModal"
    :title="`Delete ${multiple ? `${numLists} ` : ''}${entityName}?`"
    :description="`Are you sure you want to delete ${multiple ? `all ${numLists} of these ${entityName}` : reportName}?`"
    primary-button-text="Delete"
    primary-button-type="danger"
    secondary-button-text="Don't delete"
    :width="400"
    :loading="loading"
    :action-loading="loading"
    variant="confirm"
    @action="() => onDelete(reportToDelete)"
    @closed="onCloseModal"
  />
</template>

<script setup lang="ts">
import { BittsModal } from '@crossbeam/bitts';
import { Nullable } from '@crossbeam/types';

import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { BASE_ROUTES } from '@/constants/routes';
import { useFlashesStore, useReportsStore } from '@/stores';
import { Report, TrimmedReport } from '@/types/reports';

const {
  visible = false,
  report = null,
  noRedirect = false,
  parentLoading = false,
  numLists = undefined,
} = defineProps<{
  visible?: boolean;
  report?: TrimmedReport | Report | null;
  noRedirect?: boolean;
  parentLoading?: boolean;
  numLists?: number;
}>();

const emit = defineEmits<{
  closed: [];
  'reports-deleted': [];
}>();

const modalLoading = ref(false);
const showDeleteReportModal = ref(false);

const loading = computed(() => parentLoading || modalLoading.value);
const isBulk = computed(() => numLists !== undefined);
const multiple = computed(() => (numLists ?? 0) > 1);

const route = useRoute();
const router = useRouter();

const reportsStore = useReportsStore();
const flashesStore = useFlashesStore();
const entityName = computed(() => (multiple.value ? 'Lists' : 'List'));

const reportName = computed(() => {
  const name = report?.name || reportToDelete.value?.name;
  return name ? `"${name}"` : `this ${entityName.value}`;
});

const currentReportId = computed(
  () =>
    route.params.report_id /* from ReportResults */ ||
    route.query.id /* from ReportOverflowMenu */,
);
const reportToDelete = computed<Nullable<TrimmedReport | Report>>(() => {
  if (isBulk.value) return null;
  if (report) return report;
  return reportsStore.getByReportId(currentReportId.value as string) || null;
});

const folderId = computed(() => route.params.folder_id);
const closeRoute = computed(() => {
  if (route.name?.toString().includes('partner_details')) {
    return {
      name: 'partner_details',
      params: { partner_org_id: route.params.partner_org_id },
    };
  } else if (route.name?.toString().includes('lists')) {
    return { name: 'lists' };
  }
  if (!folderId.value) {
    return { name: 'lists' };
  }
  return {
    name: BASE_ROUTES.REPORT_FOLDER,
    params: { folder_id: folderId.value },
  };
});

onMounted(async () => {
  if (report) return;
  if (isBulk.value) {
    showDeleteReportModal.value = true;
    return;
  }
  modalLoading.value = true;
  await reportsStore.refreshReportsStore();
  showDeleteReportModal.value = true;
  modalLoading.value = false;
});

async function onCloseModal() {
  showDeleteReportModal.value = false;
  if (noRedirect) {
    emit('closed');
  } else {
    await router.push(closeRoute.value);
  }
}

async function onDelete(report: Nullable<TrimmedReport | Report>) {
  if (!report || isBulk.value) {
    emit('reports-deleted');
    return;
  }
  modalLoading.value = true;
  try {
    await reportsStore.deleteReport({
      ...report,
      id: currentReportId.value?.toString() || '',
    });
    flashesStore.addSuccessFlash({
      message: `"${report?.name}" ${entityName.value} deleted`,
    });
    await reportsStore.refreshReportsStore();
  } catch (_err) {
    flashesStore.addErrorFlash({
      message: `Failed to delete ${entityName.value}`,
    });
  } finally {
    onCloseModal();
    modalLoading.value = false;
    if (noRedirect) {
      await router.push(closeRoute.value);
    }
  }
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.o-bitts-modal:deep()
  dialog
  .o-bitts-modal__header
  .o-bitts-modal__description {
  @apply mb-0;
}
</style>
